import {
    checkSlashIDOptions,
    createOIDCOptionsForIdPInitiatedSSO,
    createSlashIDConfig,
    getIdentityProviderInitiatedSSOParams,
    SlashIDConfig,
    SlashIDOptions,
} from "../domain/options"
import { BaseUser } from "./user.base"

import * as api from "../clients/slashid-api"
import * as domain from "../domain/index"
import * as internalErrors from "../domain/errors.internal"
import * as errors from "../domain/errors"
import * as base64 from "../utils/base64"
import * as events from "../utils/events"
import { versionInfo as version } from "../version"
import { CredentialsStorageV2, CrossDomainStorage, IdentifiersStorage } from "./storage"
import { compareURLsByOriginAndPathname } from "../utils/url"
import { OrganizationID, UserTokenText } from "../domain/index"
import { Configuration, DefaultConfig } from "../clients/slashid-api"
import { BrowserUser } from "./user.browser"
import { BrowserAnalytics } from "./analytics/analytics.browser"
import { Flow } from "./flow"
import { ChallengeProcessor } from "../domain/challenges"
import { createEventEmitter, EventEmitter } from "../domain/emitter"
import { createRetryOnNetworkErrorMiddleware } from "../utils/fetch"
import { AnonymousUser } from "./user.anonymous"
import { slashIDOptionsToCreateAnonymousUserOptions, createAnonymousUserToken } from "./user.utils"
import {
    cleanUpRedirectFlow,
    getOAuthParams,
    getRedirectFlowParams,
    LogInWithOAuthParams,
    obtainAccessToken,
    prepareCSRFToken,
    prepareRedirectFlow,
} from "../domain/sso"
import { ERROR_NAMES } from "../domain/errors"

const SlashIDIFrameID: string = "__SlashID_iframe"

type Challenge =
    | api.ProxyChallenge
    | api.NonceChallenge
    | api.WebAuthnGetChallenge
    | api.WebAuthnCreateChallenge
    | api.OIDCChallenge

const PRIVATE_PUBLISH = Symbol()
const PRIVATE_SUBSCRIBE = Symbol()
const PRIVATE_UNSUBSCRIBE = Symbol()

export const __INTERNAL_ONLY = (sid: SlashID) => ({
    publish: <Key extends keyof events.PrivateEvents>(type: Key, event: events.PrivateEvents[Key]) =>
        sid[PRIVATE_PUBLISH](type, event),
    subscribe: <Key extends keyof events.PrivateEvents>(
        type: Key,
        handler: events.Handler<events.PrivateEvents[Key]>
    ) => sid[PRIVATE_SUBSCRIBE](type, handler),
    unsubscribe: <Key extends keyof events.PrivateEvents>(
        type: Key,
        handler: events.Handler<events.PrivateEvents[Key]>
    ) => sid[PRIVATE_UNSUBSCRIBE](type, handler),
})

/**
 * This class is the main entry point for users of the SlashID SDK. It exposes a single function
 * for uniform access to all supported identification methods as {@link id}.
 * The returned {@link Types.BaseUser} object has methods to either register further
 * user handles (e.g. a second e-mail address, or a phone number), or ask the user to
 * authenticate using any number of available authentication methods. Please see {@link Types.BaseUser}
 * for further details and examples.
 *
 * For a quick start please consult the {@link constructor} and {@link id} documentation and examples.
 */
export class SlashID {
    public readonly baseURL: string
    public readonly sdkURL: string
    public readonly oid?: string
    private readonly emitter: EventEmitter<events.NamedEvents>
    private readonly config: SlashIDConfig

    private authFlow: Flow | null
    private recoveryFlow: Flow | null
    public authnClient: api.DefaultApi
    public personsClient: api.PersonsApi
    public iframe: HTMLIFrameElement | null
    public identifiersStorage: IdentifiersStorage
    public credentialStorage: CredentialsStorageV2
    private analytics: BrowserAnalytics | undefined
    private emitterDecoratorFactory: events.LegacyHandlerDecoratorFactory<keyof events.NamesToEventsMap>

    private userFromURL: BaseUser | null

    /**
     * @example
     *
     * For production you can simply create your _SlashID_ instance with:
     *
     * ```js
     * import * as slashid from "slashid"
     * const sid = new slashid.SlashID()
     * ```
     *
     * If you want to experiment and connect to the sandbox environment instead:
     *
     * ```js
     * const sid = new slashid.SlashID({
     *   environment: "sandbox"
     * })
     * ```
     *
     * @param options Your SlashID connection options.
     * @throws `TypeError` If the given `options`, if defined, fail validation
     */
    constructor(options?: SlashIDOptions) {
        if (options) {
            checkSlashIDOptions(options)
        }

        this.config = createSlashIDConfig(options ?? {})
        const { baseURL, sdkURL, oid, analyticsEnabled = true } = this.config

        this.baseURL = baseURL
        this.sdkURL = sdkURL
        this.oid = oid
        this.authFlow = null
        this.recoveryFlow = null

        this.authnClient = new api.DefaultApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
            })
        )

        this.personsClient = new api.PersonsApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
            })
        )

        this.iframe = this.setupIFrame()
        const crossDomainStore = new CrossDomainStorage(this.iframe, this.getSDKUrl().origin)
        this.identifiersStorage = new IdentifiersStorage(crossDomainStore)
        this.credentialStorage = new CredentialsStorageV2(crossDomainStore)
        this.userFromURL = null
        this.emitter = createEventEmitter()
        this.emitter.use(this.createFlowScopeMiddleware())
        this.emitterDecoratorFactory = events.createLegacyHandlerDecoratorFactory()

        this.analytics = analyticsEnabled
            ? new BrowserAnalytics({
                  sdk: this,
              })
            : undefined

        this.listenForEvents()
    }
    /**
     * Get an instance of the Analytics class. Use the instance to track virtual page views.
     * @throws `Error` If analytics is not enabled or oid is not set.
     * @returns Analytics instance
     */
    public getAnalytics(): BrowserAnalytics {
        if (!this.analytics) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                message: "Analytics has to be enabled to use this method",
            })
        }
        return this.analytics
    }

    public static async canGetPublicKeyCredentialsInIFrame(): Promise<boolean> {
        const jsWindow = window as any
        const isChrome = !!jsWindow.chrome && (!!jsWindow.chrome.webstore || !!jsWindow.chrome.runtime)
        return isChrome
    }

    /**
     * Starts the login flow with a redirect to the hosted login page.
     */
    public async startHostedLoginFlow(params: LogInWithOAuthParams) {
        // ensure the session storage is ready before starting a new flow
        prepareRedirectFlow(params)
        const { csrf_token, pkce_code_challenge } = await prepareCSRFToken()

        const url = new URL("/oauth2/authorize", this.baseURL)
        url.searchParams.set("redirect_uri", params.redirectUri)
        url.searchParams.set("client_id", params.clientId)
        url.searchParams.set("response_type", "code")
        url.searchParams.set("state", csrf_token)
        url.searchParams.set("scope", "openid")
        url.searchParams.set("code_challenge", pkce_code_challenge)
        url.searchParams.set("code_challenge_method", "S256")

        // navigate
        window.location.assign(url)
    }

    /**
     * Resolves the previously started login flow with a redirect to the hosted login page.
     * Call this method on the page where the hosted login page redirects to.
     */
    public async resolveHostedLoginFlow(): Promise<BrowserUser | undefined> {
        const oidcParams = getOAuthParams(window.location.search)
        if (oidcParams) {
            const { clientId, redirectUri } = getRedirectFlowParams()
            // POST to /oauth2/tokens
            // TODO no API client for this
            try {
                const { id_token } = await obtainAccessToken({
                    ...oidcParams,
                    clientId,
                    redirectUri,
                    apiBaseURL: this.baseURL,
                })
                const user = this.getUser(id_token)

                if (this.analytics) {
                    this.getAnalytics().identify(user)
                }

                cleanUpRedirectFlow()

                return user
            } catch (e: any) {
                await this.processError(e, {})
                throw await internalErrors.toSlashIDError(e)
            }
        }
    }

    /**
     * Event emitter middleware that prevents events leaking between flows
     */
    private createFlowScopeMiddleware() {
        const sdk = this

        return function middleware(event: events.NamedEvents, cancel: () => void) {
            if (
                event.meta &&
                event.meta.authFlowId &&
                sdk.authFlow?.flowId &&
                sdk.authFlow?.flowId !== event.meta.authFlowId
            ) {
                return cancel()
            }

            if (
                event.meta &&
                event.meta.recoveryFlowId &&
                sdk.recoveryFlow?.flowId &&
                sdk.recoveryFlow?.flowId !== event.meta.recoveryFlowId
            ) {
                return cancel()
            }

            return event
        }
    }

    /**
     * Publish an event to the SDK. Currently available events are listed in {@link Types.PublicWriteEvents}.
     * @param type
     * @param event
     */
    public publish<Key extends keyof events.PublicWriteEvents>(type: Key, event: events.PublicWriteEvents[Key]) {
        if (!events.isPublicWriteEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to publish an unsupported event: ${type}`,
                context: { type, event },
            })
        }

        this.emitter.emit({
            name: type,
            payload: event,
            meta: { access: "public", authFlowId: this.authFlow?.flowId, recoveryFlowId: this.recoveryFlow?.flowId },
        } as events.NamedEvents)
    }

    /**
     * Variant of [publish] for private events
     */
    [PRIVATE_PUBLISH]<Key extends keyof events.PrivateEvents>(type: Key, event: events.PrivateEvents[Key]) {
        if (!events.isPrivateEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to publish an unsupported event: ${type}`,
                context: { type, event },
            })
        }

        this.emitter.emit({
            name: type,
            payload: event,
            meta: { access: "private", authFlowId: this.authFlow?.flowId, recoveryFlowId: this.recoveryFlow?.flowId },
        })
    }

    /**
     * Subscribe to the events published by the SDK. Currently available events are listed in {@link Types.PublicReadEvents}.
     * @param type
     * @param handler
     */
    public subscribe<Key extends keyof events.PublicReadEvents>(
        type: Key,
        handler: events.Handler<events.PublicReadEvents[Key]>
    ) {
        if (!events.isPublicReadEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to subscribe to unsupported event: ${type}`,
                context: { type },
            })
        }

        // TODO type check fails because of event payload types that are void or string
        // @ts-expect-error
        const wrappedHandler = this.emitterDecoratorFactory.decorate(handler)
        this.emitter.on(type, wrappedHandler)
    }

    /**
     * Variant of [subscribe] for private events
     */
    [PRIVATE_SUBSCRIBE]<Key extends keyof events.PrivateEvents>(
        type: Key,
        handler: events.Handler<events.PrivateEvents[Key]>
    ) {
        if (!events.isPrivateEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to subscribe to unsupported event: ${type}`,
                context: { type },
            })
        }
        // TODO type check fails because of event payload types that are void or string
        // @ts-expect-error
        const wrappedHandler = this.emitterDecoratorFactory.decorate(handler)
        this.emitter.on(type, wrappedHandler)
    }

    /**
     * Unsubscribe from the SDK events. Currently available events are listed in {@link Types.PublicReadEvents}.
     * @param type
     * @param handler
     */
    public unsubscribe<Key extends keyof events.PublicReadEvents>(
        type: Key,
        handler: events.Handler<events.PublicReadEvents[Key]>
    ) {
        if (!events.isPublicReadEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to unsubscribe from an unsupported event: ${type}`,
                context: { type },
            })
        }

        // TODO type check fails because of event payload types that are void or string
        // @ts-expect-error
        const decoratedHandler = this.emitterDecoratorFactory.getDecorator(handler)

        if (decoratedHandler) {
            this.emitter.off(type, decoratedHandler)
        }
    }

    /**
     * Variant of [subscribe] for private events
     */
    [PRIVATE_UNSUBSCRIBE]<Key extends keyof events.PrivateEvents>(
        type: Key,
        handler: events.Handler<events.PrivateEvents[Key]>
    ) {
        if (!events.isPrivateEvent(type)) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.unknown,
                message: `Trying to unsubscribe from an unsupported event: ${type}`,
                context: { type },
            })
        }

        // TODO type check fails because of event payload types that are void or string
        // @ts-expect-error
        const decoratedHandler = this.emitterDecoratorFactory.getDecorator(handler)

        if (decoratedHandler) {
            this.emitter.off(type, decoratedHandler)
        }
    }

    /**
     * Get a Promise that resolves to an array of challenge objects.
     */
    public async getChallengesFromURL(): Promise<api.ChallengeListInner[] | null> {
        const queryString: string = window.location.search
        const urlParams = new URLSearchParams(queryString)

        let challengeObjs = undefined

        const challengePackId = urlParams.get(domain.ChallengePackURLOption)
        const encodedChallenges = urlParams.get(domain.ChallengesURLOption)

        if (challengePackId && encodedChallenges) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.invalidSlashIDURLParamsError,
                message: "found both 'challenges' and 'sidcp' URL query parameters",
                context: { challengePackId, encodedChallenges, queryString },
            })
        }

        if (challengePackId) {
            try {
                // we got a challenge pack ID => get the challenges
                const response = await this.authnClient.getChallengePackChallengePackId(
                    { challengePackId, slashIDSdkVersion: version.raw },
                    {
                        cache: "no-cache",
                    }
                )
                if (response.errors) {
                    throw new internalErrors.SlashIDError({
                        name: ERROR_NAMES.apiResponseError,
                        message: "error fetching challenges",
                        context: { errors: response.errors },
                    })
                }
                challengeObjs = response.result
            } catch (e) {
                throw await internalErrors.toSlashIDError(e)
            }
        } else if (encodedChallenges) {
            // check whether challenges are encoded directly in the params
            const challengesData = base64.decode(encodedChallenges)
            const challengesStr = new TextDecoder("utf-8").decode(challengesData)
            challengeObjs = JSON.parse(challengesStr)
        }

        return challengeObjs
    }

    /**
     * Process authentication challenges from the URL query parameters.
     * You can use this in conjunction with the Direct-ID API to allow your users to land on your
     * target page already authenticated.
     *
     * If Identity Provider Initiated SSO is enabled and the organization ID is set,
     * this method will also start the SSO flow given the correct parameters.
     *
     * @returns The authenticated user from the `challenges` URL parameter, if present, `null` otherwise.
     */
    public async getUserFromURL(): Promise<BaseUser | null> {
        // processing challenges consumes them, it's not idempotent => on success we memoize the result
        if (!this.userFromURL) {
            // decide what to process

            // #1 IdP-initiated flow
            if (this.config.identityProviderInitiatedSSOEnabled && this.oid) {
                const idpInitiatedSSOParams = getIdentityProviderInitiatedSSOParams(window.location.search)
                if (idpInitiatedSSOParams) {
                    try {
                        const ssoOptions = await createOIDCOptionsForIdPInitiatedSSO(idpInitiatedSSOParams)
                        this._id(this.oid, undefined, ssoOptions)
                    } catch (e: any) {
                        await this.processError(e, {})
                        throw await internalErrors.toSlashIDError(e)
                    }
                }
            }

            // #2 challenges - regular flow
            let challengeObjs: Challenge[] | null
            try {
                challengeObjs = await this.getChallengesFromURL()
            } catch (e: any) {
                await this.processError(e, {})
                throw await internalErrors.toSlashIDError(e)
            }

            if (challengeObjs) {
                const ssoParams = domain.getSSOChallengeFromURL(window.location.search)
                if (ssoParams) {
                    this.resolveSSOChallenge(ssoParams) // should not block challenge processing
                }

                let token: UserTokenText | undefined = undefined

                try {
                    const challengeProcessor = new ChallengeProcessor(this, this.emitter)
                    token = await challengeProcessor.processChallenges({
                        challenges: challengeObjs,
                    })
                } catch (e: any) {
                    // check if we could extract the challenge ID from the
                    await this.processError(e, {})

                    const isHandlePatternNotAllowedError =
                        internalErrors.isResponseError(e) && (await internalErrors.isHandlePatternNotAllowedError(e))
                    if (isHandlePatternNotAllowedError) {
                        throw new internalErrors.SlashIDError({
                            name: errors.ERROR_NAMES.handlePatternNotAllowed,
                            message: e.message,
                            cause: e,
                        })
                    }

                    const isSignUpAwaitingApprovalError =
                        internalErrors.isResponseError(e) && (await internalErrors.isSignUpAwaitingApprovalError(e))
                    if (isSignUpAwaitingApprovalError) {
                        throw new internalErrors.SlashIDError({
                            name: errors.ERROR_NAMES.signUpAwaitingApproval,
                            message: e.message,
                            cause: e,
                        })
                    }

                    throw await internalErrors.toSlashIDError(e)
                }

                this.userFromURL = new BaseUser(token!!, {
                    baseURL: this.baseURL,
                    sdkURL: this.sdkURL,
                })

                if (this.userFromURL.authentications?.length) {
                    const lastAuthentication =
                        this.userFromURL.authentications[this.userFromURL.authentications.length - 1]

                    this.emitter.emit({
                        name: "idFlowSucceeded",
                        payload: {
                            token: token!!,
                            authenticationFactor: { method: lastAuthentication.method } as domain.Factor,
                            handle: lastAuthentication.handle,
                        },
                        meta: { access: "public" },
                    })
                }
            }
        }

        if (this.userFromURL) {
            this.emitter.emit({
                name: "userAuthenticatedFromURL",
                payload: { token: this.userFromURL.token },
                meta: { access: "public" },
            })
        }

        return this.userFromURL
    }

    /**
     * This method first checks for URL params associated with SSO. If they are present, it:
     *  - performs CSRF protection checks
     *  - resolves SSO challenge
     * @returns void
     */
    private async resolveSSOChallenge({ ssoChallengeID, ssoCode, csrfToken }: domain.SSOChallengeParams) {
        if (!ssoChallengeID || !ssoCode || !csrfToken) {
            return
        }

        try {
            const storedCsrfToken = window.localStorage.getItem(domain.StorageKeyCSRFToken)
            if (!storedCsrfToken) {
                throw new Error("no CSRF token stored")
            }

            if (csrfToken !== storedCsrfToken) {
                throw new Error("CSRF token mismatch")
            }

            const pkceCodeVerifier = window.localStorage.getItem(domain.StorageKeyPKCECodeVerifier)
            if (!pkceCodeVerifier) {
                throw new Error("no PKCE code verifier stored")
            }

            await this.authnClient.postSsoResolve({
                slashIDOrgID: this.oid!,
                postSsoResolveRequest: {
                    challenge_id: ssoChallengeID,
                    code: ssoCode,
                    pkce_code_verifier: pkceCodeVerifier,
                },
            })
        } catch (err) {
            this.processError(err, {})
        } finally {
            window.localStorage.removeItem(domain.StorageKeyCSRFToken)
            window.localStorage.removeItem(domain.StorageKeyPKCECodeVerifier)
        }
    }

    private async processEvent(event: MessageEvent<domain.MessageEventPayload>) {
        // CSRF protection for SSO popup mode
        if (domain.isSSOChallengeMessage(event.data)) {
            if (event.origin !== this.baseURL) {
                return
            }

            const ssoParams = domain.getSSOChallengeFromURL(event.data.urlParams)
            if (ssoParams) {
                await this.resolveSSOChallenge(ssoParams)
            }

            return
        }

        let sourceWindow: Window | null = null
        if (window.opener && event.source === window.opener) {
            sourceWindow = window.opener
        }
        if (window.parent && event.source === window.parent) {
            sourceWindow = window.parent
        }

        if (!sourceWindow) {
            return
        }

        if (domain.isPingMessage(event.data)) {
            const request: domain.PingRequestMessage = event.data
            try {
                sourceWindow.postMessage(new domain.PingResponseMessage(request.id), "*")
            } catch (e: any) {
                sourceWindow.postMessage(new domain.PingResponseMessage(request.id, e.toString()), "*")
            }
        }

        if (domain.isWebAuthnCreateRequestMessage(event.data)) {
            const request: domain.WebAuthnCreateRequestMessage = event.data
            try {
                const challengeProcessor = new ChallengeProcessor(this, this.emitter)
                const attestation = await challengeProcessor.processWebAuthnCreateChallenge(request.challenge)
                sourceWindow.postMessage(new domain.WebAuthnCreateResponseMessage(request.id, attestation), "*")
            } catch (e: any) {
                sourceWindow.postMessage(new domain.WebAuthnCreateResponseMessage(request.id, null, e.toString()), "*")
            }
        }

        if (domain.isWebAuthnGetRequestMessage(event.data)) {
            const request: domain.WebAuthnGetRequestMessage = event.data
            try {
                const challengeProcessor = new ChallengeProcessor(this, this.emitter)
                const attestation = await challengeProcessor.processWebAuthnGetChallenge(request.challenge)
                sourceWindow.postMessage(new domain.WebAuthnGetResponseMessage(request.id, attestation), "*")
            } catch (e: any) {
                sourceWindow.postMessage(new domain.WebAuthnGetResponseMessage(request.id, null, e.toString()), "*")
            }
        }
    }

    private async listenForEvents() {
        window.addEventListener("message", this.processEvent.bind(this))
    }

    private setupIFrame(): HTMLIFrameElement | null {
        const sdkURLStr = this.getSDKUrl().toString()

        // No need, we're already in the target context
        if (compareURLsByOriginAndPathname(new URL(window.location.href), new URL(this.sdkURL))) {
            return null
        }

        // Try to find an existing one by ID
        let slashIDIFrame: HTMLIFrameElement | null = document.getElementById(SlashIDIFrameID) as HTMLIFrameElement

        if (slashIDIFrame && slashIDIFrame.src !== sdkURLStr) {
            slashIDIFrame.remove()
            slashIDIFrame = null
        }

        if (!slashIDIFrame) {
            // Not found, create an invisible one
            slashIDIFrame = document.createElement("iframe")
            slashIDIFrame.id = SlashIDIFrameID
            slashIDIFrame.allow = "publickey-credentials-get *"
            slashIDIFrame.src = sdkURLStr
            slashIDIFrame.style.display = "none"
            document.body.appendChild(slashIDIFrame)
        }

        return slashIDIFrame
    }

    /**
     * List the available authentication methods for the chosen handle type. Currently available
     * authentication methods are listed in {@link Types.FactorMethod}. Before choosing a method to pass
     * to {@link id} you should consult the result of this method. In particular the availability of
     * the {@link Types.FactorMethod['Webauthn']} method depends on the current device configuration and
     * might not always be available.
     *
     * It's important to note that you can rely on some authentication methods to always be available:
     *
     *  - for {@link PersonHandleType['EmailAddress']}: {@link Types.FactorMethod['OtpViaEmail']} and
     *    {@link Types.FactorMethod['EmailLink']} are always available;
     *  - for {@link PersonHandleType['PhoneNumber']}: {@link Types.FactorMethod['OtpViaSms']} and
     *    {@link Types.FactorMethod['SmsLink']} are always available;
     *
     * On the contrary, the availability of {@link Types.FactorMethod['Webauthn']} depends exclusively on the
     * current device configuration.
     *
     * @remarks The availability of authetication methods can vary over time, you probably should not
     * cache or store the result and instead invoke the method any time you need to authenticate a user.
     *
     * @example
     *
     * SlashID supports a number of handle types, each in turn supporting a set of
     * authentication methods. Supposing you identify your users with e-mail addresses,
     * you should check the authentication methods available on the current device before
     * kicking off the authentication flow:
     *
     * ```js
     * const availableMethods = sid.getAvailableAuthenticationMethods("email_address")
     * // => availableMethods === ["webauthn", "email_link"]
     * ```
     *
     * At this point you can choose which method to use in {@link id}. You could decide based on
     * one or a combination of:
     *
     *  - Your static preference: e.g. require WebAuthn, prefer WebAuthn ({@link Types.FactorMethod['Webauthn']})
     *    if available, otherwise fallback to magic link via e-mail ({@link Types.FactorMethod['EmailLink']})
     *    ```
     *    const chosenMethod = availableMethods.includes("webauthn") ? "webauthn" : "email_link"
     *    ```
     *  - User's choice: you could present your users with a selection of the available methods;
     *
     * In general the SlashID SDK does not impose any restriction on how you decide to choose which
     * authentication method to use.
     *
     * @param handleType Available handle types are ``"email_address"``, ``"phone_number"`` and ``"username"``.
     * @returns  An array of the available authentication methods on this device.
     */
    public async getAvailableAuthenticationMethods(handleType: api.PersonHandleType): Promise<api.FactorMethod[]> {
        const availableMethods: api.FactorMethod[] = [api.FactorMethod.Password, api.FactorMethod.Totp]

        switch (handleType) {
            case api.PersonHandleType.EmailAddress:
                availableMethods.push(api.FactorMethod.EmailLink, api.FactorMethod.OtpViaEmail)
                break
            case api.PersonHandleType.PhoneNumber:
                availableMethods.push(api.FactorMethod.SmsLink, api.FactorMethod.OtpViaSms)
                break
            case api.PersonHandleType.Username:
                break
            default:
                throw new internalErrors.SlashIDError({
                    name: ERROR_NAMES.unknown,
                    message: `unhandled handle type: ${handleType}`,
                })
        }

        if (navigator && window.PublicKeyCredential && domain.isReachablePersonHandleType(handleType)) {
            availableMethods.unshift(api.FactorMethod.Webauthn)
        }

        return availableMethods
    }

    /**
     * Preferred way of creating  a {@link Types.BaseUser} instance based on an existing token.
     *
     * @param token A user {@link Types.UserTokenText} value.
     * @returns User instance
     */
    public getUser(token: UserTokenText): BrowserUser {
        const user = new BrowserUser(token, this)
        return user
    }

    /**
     * Identify a user. This method implements the SlashID authentication flow.
     * In particular the flow is:
     *
     *  - **passwordless**: all you need to specify to authenticate a user is their handle,
     *    be it an e-mail address or phone number, and how they wish to authenticate; please see
     *    the parameters documentation for more details;
     *
     *  - **unified**: it does not require you to differentiate between first registration and
     *    subsequent logins attempts. The SlashID service takes care of the distinction and will
     *    perform the necessary verification ceremonies on your behalf under the hood.
     *
     *  - **synchronous**: on successful return the user has verified their identity; even when
     *    the authentication spans multiple devices and is comprised of multiple steps (e.g
     *    clicking a magic link on the provided e-mail address and creating a new biometric
     *    credential on the current device), this method will not return until all steps have
     *    completed or errors have been encountered.
     *
     *
     * Before utilizing any authentication method you should check whether it is available with {@link getAvailableAuthenticationMethods}.
     *
     * The currently available options for the `authenticationFactor.method` field are:
     *
     *  - **``"webauthn"``**: Authenticate the user via WebAuthn on this device. If available this
     *    usually entails built-in biometric authentication or the use of an external FIDO key, with a
     *    preference for the former. Please also see the [WebAuthn scope](#webauthn_scope) and
     *    [WebAuthn attachment](#webauthn_attachment) sections below for details.
     *
     *  - **``"email_link"``**: Deliver a magic link via e-mail.
     *
     *  - **``"otp_via_email"``**: Deliver an OTP security code via e-mail. This authentication method requires you to {@link publish} an
     *    `otpCodeSubmitted` event to the SDK. The event payload must be a string containing the OTP
     *    value, which your user will receive via e-mail.
     *
     *  - **``"sms_link"``**: Deliver a magic link via SMS.
     *
     *  - **``"otp_via_sms"``**: Deliver an OTP security code via SMS. This authentication method requires you to {@link publish} an
     *    `otpCodeSubmitted` event to the SDK. The event payload must be a string containing the OTP
     *    value, which your user will receive via SMS.
     *
     *  - **``oidc``**: Authenticate the user using OIDC through a preconfigured Identity Provider.
     *
     *  - **``totp``**: Authenticate the user with a TOTP code. This authentication method requires you to {@link publish} an
     *    `otpCodeSubmitted` event to the SDK. The event payload must be a string containing the OTP
     *    code, which your user will retrieve from their authenticator app or device.
     *
     * <a name="webauthn_scope"></a>
     * <b>WebAuthn scope</b>
     *
     * In case you have chosen the WebAuthn method, you have the option to specify a `scope`
     * in the `options` field. This field allows you to control the breadth of your users' WebAuthn credentials.
     * The field is optional, but if specified it can either be your domain or any "registrable domain suffix" of it.
     * E.g. if your domain is `shop.domain.com` you have two options to choose from:
     *
     *  - `shop.domain.com`: scope your users to the current domain. Credentials created here will not be shared with sibling domains such as `services.domain.com`;
     *  - `domain.com`: scope your users to the least specific domain. Credentials created here will be allowed to access all subdomains such as `services.domain.com`;
     *
     * Please consult the HTML spec for the formal definition of [registrable domain suffix](https://html.spec.whatwg.org/multipage/origin.html#is-a-registrable-domain-suffix-of-or-is-equal-to)
     * if you need detailed information. When not specified the `scope` option defaults to the current origin, `shop.domain.com`
     * in the example above.
     *
     * <a name="webauthn_attachment"></a>
     * <b>WebAuthn attachment</b>
     *
     * Users can perform WebAuthn either with built-in authenticators (FaceID, TouchID, fingerprint readers, etc.)
     * or external security keys, and in case you have a preference you can control which authenticator type to use
     * by specifying an `attachment` option field in the `options` field.
     * The field is optional and has 3 allowed values from the {@link WebAuthnAuthenticatorAttachment} enum:
     *
     *  - `"platform"`: mandate use of built-in authenticators;
     *  - `"cross-platform"`: mandate use of external security keys;
     *  - `"any"`: the default value if unspecified, allows both types;
     *
     * If left unspecified (or explicitly set to `"any"`) the user will be prompted with a system dialog to choose
     * which type of authenticator they prefer. By providing one of the other options instead (e.g. `"platform"`)
     * you explicitly limit the authenticator types the user can choose from. On most devices this results in the
     * most seamless WebAuthn user experience possible, as the system dialog will not be shown and the user will be
     * prompted to authenticate directly.
     *
     * @param oid Your organization ID.
     * @param handle How the user wishes to identify, e.g. their e-mail address.
     * @param authenticationFactor How to verify the user's identity matches the handle.
     *
     * @throws `slashid.errors_INTERNAL_ONLY.InvalidAuthenticationMethodError` if the chosen method is
     * incompatible with the given handle or is not available on the current device.
     *
     * @fires {@link Types.IdFlowSucceededEvent} when the flow resolves successfully
     *
     * @returns The authenticated user.
     *
     * @example
     *
     * Authenticating a user is as simple as:
     *
     * ```js
     * const user = await slashID.id({
     *   type: "phone_number",
     *   value: "+13337777777",
     * }, {
     *   method: "webauthn"
     * })
     * ```
     *
     * At this point the user has been authenticated; either registered or logged in.
     *
     * You can use it in a header in your HTTP requests:
     *
     * ```js
     * const response = await fetch(url, {
     *   ...
     *   headers: {
     *     'Authorization': `Bearer ${user.token}`,
     *      ...
     *   }
     * });
     * ```
     *
     * You can collect and associate user attributes:
     *
     * ```js
     * const userDetails = { name: "...", streetAddress: "..." }
     * await user.set(userDetails)
     * ```
     *
     * You can perform multi-factor authentication before proceeding with your application logic:
     *
     * ```js
     * await user.mfa({
     *   type: "email_address",
     *   value: "my.email@example.com"
     * }, {
     *   method: "email_link"
     * })
     * ```
     */
    public async id(
        oid: OrganizationID,
        handle: api.PersonHandle,
        authenticationFactor: domain.Factor
    ): Promise<BrowserUser> {
        return this._id(oid, handle, authenticationFactor)
    }

    private createAuthFlow(): Flow {
        const flow = new Flow(this, this.emitter)
        this.authFlow = flow

        return flow
    }

    private clearAuthFlow(flow: Flow) {
        if (this.authFlow === flow) {
            this.authFlow = null
        }
    }

    private createRecoveryFlow(): Flow {
        const flow = new Flow(this, this.emitter)
        this.recoveryFlow = flow

        return flow
    }

    private clearRecoveryFlow(flow: Flow) {
        if (this.recoveryFlow === flow) {
            this.recoveryFlow = null
        }
    }

    /**
     * @internal
     */
    public async _id(
        oid: OrganizationID,
        handle?: api.PersonHandle,
        authenticationFactor?: domain.Factor,
        prevToken?: UserTokenText
    ): Promise<BrowserUser> {
        const flow = this.createAuthFlow()

        try {
            const user = await flow._id(oid, handle, authenticationFactor, prevToken)
            return user
        } catch (e) {
            await this.processError(e, { authenticationFactor, handle, previousToken: prevToken })

            if (internalErrors.isResponseError(e)) {
                throw await internalErrors.createSlashIDAPIResponseError(e, { handle, authenticationFactor, oid })
            }

            throw await internalErrors.toSlashIDError(e)
        } finally {
            this.clearAuthFlow(flow)
        }
    }

    /**
     * List the handles previously used to authenticate successfully.
     *
     * @remarks Use of this method is in no way required to authenticate with SlashID. The SDK
     * provides it to allow you to more easily implement loging suggestion drop-down lists
     * and similar UX niceties, by e.g. tying it to a `<datalist>`:
     *
     * ```js
     * const handles = await sid.getAvailableIdentifiers()
     * const options = handles.map((handle) => {
     *   const option = document.createElement("option")
     *   option.value = handle.value
     *   return option
     * })
     * const datalist = document.getElementById("available_identifiers")
     * datalist.replaceChildren(...options)
     *
     * ...
     *
     * <input ... list="available_identifiers" />
     * ```
     *
     * Depending on browser settings, it may not be possible to discover these
     * identifiers.
     *
     * @returns An array of identifiers of previously-authenticated users.
     */
    public async getAvailableIdentifiers(): Promise<api.PersonHandle[]> {
        try {
            return this.identifiersStorage.getAll()
        } catch (e) {
            console.warn(e)
            return []
        }
    }

    /**
     * Use a verified handle and a factor to start the account recovery flow.
     * The user will receive instructions on how to proceed using a delivery mechanism based on the given handle.
     * After this method resolves, the user will be able to authenticate using the same handle and factor.
     */
    public async recover({
        handle,
        factor,
    }: {
        handle: domain.ReachablePersonHandle
        factor: api.RecoverableFactor
    }): Promise<void> {
        if (!this.oid) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                message: "oid is required - please initialize the SDK with an organization ID",
            })
        }

        // @ts-expect-error ensure this works in runtime if someone ignores TypeScript
        if (handle.type === api.PersonHandleType.Username) {
            throw new internalErrors.SlashIDError({
                name: ERROR_NAMES.recoverNonReachableHandleType,
                message: "Username is not a reachable handle type",
            })
        }

        const flow = this.createRecoveryFlow()
        this.emitter.emit({
            name: "recoveryFlowStarted",
            payload: { handle, authenticationFactor: factor },
            meta: {
                access: "public",
                recoveryFlowId: flow.flowId,
            },
        })

        try {
            await flow.recover({ handle, factor })

            this.recoveryFlow = null
            this.emitter.emit({
                name: "recoveryFlowSucceeded",
                payload: { handle, authenticationFactor: factor },
                meta: { access: "public", recoveryFlowId: flow.flowId },
            })
        } catch (error) {
            const failureDetail = await internalErrors.getErrorMessage(error)

            const errorEvent: events.RecoveryFlowFailedEvent = {
                errorText: "recovery_failed",
                authenticationFactor: factor,
                handle,
                failureDetail,
            }

            this.emitter.emit({
                name: "recoveryFlowFailed",
                payload: errorEvent,
                meta: { access: "public", recoveryFlowId: flow.flowId },
            })

            throw await internalErrors.toSlashIDError(error)
        } finally {
            this.clearRecoveryFlow(flow)
        }
    }

    public getSDKUrl(): URL {
        const sdkURL: URL = new URL(this.sdkURL)
        sdkURL.searchParams.append("v", version.raw)
        return sdkURL
    }

    public isLocalScope(rpID: string): boolean {
        return window.location.hostname.endsWith(rpID)
    }

    /**
     * Examine the error object and publish the proper event as a reaction to it.
     */
    private async processError(
        error: any,
        { authenticationFactor, handle, previousToken, challengeId }: Omit<events.FlowErrorMetadata, "failureDetail">
    ) {
        if (errors.isFlowCancelledError(error)) {
            // cancelling a flow is not an error
            // TODO later turn this into a proper event
            return
        }

        const errorEvent = await internalErrors.createEventFromError(error, {
            authenticationFactor,
            handle,
            previousToken,
            challengeId,
        })

        if (events.isClientSideErrorEvent(errorEvent)) {
            // TODO: dedupe this in @slashid/react
            const internal = __INTERNAL_ONLY(this)
            internal.publish("clientSideError", errorEvent)
        } else {
            this.emitter.emit({
                name: "idFlowFailed",
                payload: errorEvent,
                meta: {
                    access: "public",
                    authFlowId: this.authFlow?.flowId,
                    recoveryFlowId: this.recoveryFlow?.flowId,
                },
            })
        }
    }

    public async createAnonymousUser(): Promise<AnonymousUser> {
        const createAnonUserOptions = slashIDOptionsToCreateAnonymousUserOptions(this)
        const token = await createAnonymousUserToken(createAnonUserOptions)

        return new AnonymousUser(token, this)
    }
}
